import React, { useState } from 'react';
import { X, AlertTriangle, Shield, UserCog } from 'lucide-react';
import api from '@/lib/api';
import { CLASS_IMAGE } from '@/lib/game';

// Add Doctore Modal
export const AddDoctoreModal = ({ sanctumData, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Class-specific mentor skills
  const CLASS_MENTOR_SKILLS = {
    "Dimachaerus": ["strength", "speed"],
    "Provocator": ["defense", "stamina"],
    "Retiarius": ["technique", "precision"],
    "Murmillo": ["defense", "stamina"],
  };

  const getClassSkills = (className) => {
    return CLASS_MENTOR_SKILLS[className] || [];
  };

  const addDoctore = async (gladId) => {
    try {
      setLoading(true);
      setError('');

      await api.post('/sanctum/doctores', { retired_glad_id: gladId });
      await onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add doctore');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      bronze: 'text-amber-700',
      silver: 'text-gray-400',
      gold: 'text-yellow-500',
      elite: 'text-purple-500',
      legendary: 'text-orange-500'
    };
    return colors[tier] || 'text-gray-400';
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 overflow-y-auto">
      <div className="bg-gradient-to-b from-stone-900 to-stone-950 border border-amber-900/30 rounded-lg w-full max-w-lg my-4">
        {/* Header with Image */}
        <div className="bg-stone-900 border-b border-amber-900/30 z-10 rounded-t-lg">
          <div className="relative w-full h-20 overflow-hidden rounded-t-lg">
            <img 
              src="/doctore-bg.png" 
              alt="Add Doctore"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-stone-900"></div>
            
            <div className="absolute bottom-0 left-0 right-0 p-2 flex items-end justify-between">
              <h3 className="text-sm font-semibold text-amber-500">Select Retired Gladiator to Become Doctore</h3>
            </div>
            
            <button 
              onClick={onClose} 
              className="absolute top-1 right-1 p-1.5 bg-black/50 hover:bg-black/70 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-900/20 border border-red-900/50 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-red-400 text-sm">{error}</div>
          </div>
        )}

        {/* Content */}
        <div className="p-3 space-y-3 max-h-[50vh] overflow-y-auto">
          {/* Eligible Retired */}
          {sanctumData?.eligible_retired?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-400 mb-3">Eligible (50+ battles)</h4>
              <div className="space-y-2">
                {sanctumData.eligible_retired.map((glad) => {
                  const classSkills = getClassSkills(glad.class);
                  return (
                    <div
                      key={glad.id}
                      className="p-2 bg-stone-900/50 border border-amber-900/20 rounded-lg hover:border-amber-900/40 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2">
                          <img 
                            src={CLASS_IMAGE[glad.class] || CLASS_IMAGE.Provocator} 
                            alt={glad.class}
                            className="w-8 h-8 rounded-full border-2 border-amber-900/50 object-cover"
                          />
                          <div>
                            <div className="font-semibold text-amber-100 text-sm mb-0.5">{glad.name}</div>
                            <div className="text-xs text-gray-500 mb-1">
                              Age {glad.retired_age} • {glad.total_battles} battles • {glad.class}
                            </div>
                            <div className={`text-xs font-semibold ${getTierColor(glad.tier)}`}>
                              {glad.tier_name} (±{Math.round(glad.bonus_pct * 100)}%)
                            </div>
                            {classSkills.length > 0 && (
                              <div className="text-xs text-blue-400">
                                Can mentor: {classSkills.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => addDoctore(glad.id)}
                          disabled={loading}
                          className="px-4 py-2 bg-amber-900/20 hover:bg-amber-900/30 border border-amber-900/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="text-sm">Select</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ineligible Retired */}
          {sanctumData?.ineligible_retired?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-500 mb-3">⚠️ Ineligible (&lt; 50 battles)</h4>
              <div className="space-y-2">
                {sanctumData.ineligible_retired.map((glad) => (
                  <div
                    key={glad.id}
                    className="p-3 bg-stone-950/50 border border-stone-800 rounded-lg opacity-60"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-400">{glad.name}</div>
                        <div className="text-xs text-gray-600">
                          {glad.total_battles} battles (need {50 - glad.total_battles} more)
                        </div>
                      </div>
                      <span className="text-xs text-gray-600">Not eligible</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {sanctumData?.eligible_retired?.length === 0 && sanctumData?.ineligible_retired?.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-gray-500">No retired gladiators available</p>
              <p className="text-sm text-gray-600 mt-2">
                Retire gladiators with 50+ battles to convert them into doctores
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Specialize Modal
export const SpecializeModal = ({ gladiator, doctores, onClose, onSuccess }) => {
  const [selectedDoctore, setSelectedDoctore] = useState(doctores[0]?.glad_id || null);
  const [boosts, setBoosts] = useState([]);
  const [penalties, setPenalties] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const skills = ['strength', 'speed', 'defense', 'stamina', 'technique', 'precision'];

  const doctore = doctores.find(d => d.glad_id === selectedDoctore);
  const bonusPct = doctore?.bonus_pct || 0;

  const toggleBoost = (skill) => {
    if (boosts.includes(skill)) {
      setBoosts(boosts.filter(s => s !== skill));
    } else if (boosts.length < 3) {
      setBoosts([...boosts, skill]);
    }
  };

  const togglePenalty = (skill) => {
    if (penalties.includes(skill)) {
      setPenalties(penalties.filter(s => s !== skill));
    } else if (penalties.length < 3) {
      setPenalties([...penalties, skill]);
    }
  };

  const canApply = boosts.length === penalties.length && boosts.length > 0 && 
                   !boosts.some(s => penalties.includes(s));

  const applySpecialization = async () => {
    try {
      setLoading(true);
      setError('');

      await api.post(`/gladiators/${gladiator.id}/specialize`, {
        doctore_id: selectedDoctore,
        boosts,
        penalties
      });

      await onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to apply specialization');
      console.error(err);
      setShowConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  if (showConfirm) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4">
        <div className="bg-gradient-to-b from-stone-900 to-stone-950 border border-amber-900/30 rounded-lg w-full max-w-md">
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-amber-500 mb-2">Confirm Permanent Specialization</h3>
                <div className="text-sm text-gray-400 space-y-2">
                  <p><span className="text-amber-100">Gladiator:</span> {gladiator.name}</p>
                  <p><span className="text-amber-100">Doctore:</span> {doctore?.name} (±{Math.round(bonusPct * 100)}%)</p>
                </div>
              </div>
            </div>

            <div className="space-y-3 py-4 border-y border-stone-800">
              <div>
                <div className="text-sm font-semibold text-green-500 mb-1">BOOSTED SKILLS (+{Math.round(bonusPct * 100)}%):</div>
                <ul className="text-sm text-gray-400 list-disc list-inside">
                  {boosts.map(s => <li key={s} className="capitalize">{s}</li>)}
                </ul>
              </div>

              <div>
                <div className="text-sm font-semibold text-red-500 mb-1">PENALIZED SKILLS (-{Math.round(bonusPct * 100)}%):</div>
                <ul className="text-sm text-gray-400 list-disc list-inside">
                  {penalties.map(s => <li key={s} className="capitalize">{s}</li>)}
                </ul>
              </div>
            </div>

            <div className="space-y-2">
              <div className="p-3 bg-red-900/20 border border-red-900/50 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-400 space-y-1">
                    <p className="font-semibold">THIS CANNOT BE UNDONE OR CHANGED</p>
                    <p>This gladiator can NEVER use this again</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Type gladiator name to confirm:
                </label>
                <input
                  type="text"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={gladiator.name}
                  className="w-full px-4 py-2 bg-stone-900 border border-stone-700 rounded-lg text-amber-100 placeholder-gray-600 focus:outline-none focus:border-amber-900"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-900/20 border border-red-900/50 rounded-lg text-sm text-red-400">
                {error}
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={applySpecialization}
                disabled={loading || confirmName !== gladiator.name}
                className="flex-1 px-4 py-2 bg-amber-900/20 hover:bg-amber-900/30 border border-amber-900/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Applying...' : 'Confirm Specialization'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 overflow-y-auto">
      <div className="bg-gradient-to-b from-stone-900 to-stone-950 border border-amber-900/30 rounded-lg w-full max-w-xl my-4">
        {/* Header */}
        <div className="bg-stone-900 border-b border-amber-900/30 p-3 flex items-center justify-between rounded-t-lg">
          <div>
            <h3 className="text-base font-semibold text-amber-500">Specialized Training Setup</h3>
            <p className="text-xs text-gray-400">{gladiator.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-800 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[50vh] overflow-y-auto">
          {/* Warning */}
          <div className="p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-200">
              <p className="font-semibold mb-1">ONE TIME USE - Cannot be changed!</p>
              <p className="text-amber-200/80">This gladiator can only specialize once in their lifetime.</p>
            </div>
          </div>

          {/* Select Doctore */}
          <div>
            <label className="block text-sm font-semibold text-gray-400 mb-3">Select Doctore:</label>
            <div className="space-y-2">
              {doctores.map((doc) => (
                <label
                  key={doc.glad_id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedDoctore === doc.glad_id
                      ? 'bg-amber-900/20 border-amber-900/50'
                      : 'bg-stone-900/50 border-stone-800 hover:border-amber-900/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="doctore"
                    value={doc.glad_id}
                    checked={selectedDoctore === doc.glad_id}
                    onChange={() => setSelectedDoctore(doc.glad_id)}
                    className="text-amber-500"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-amber-100">{doc.name}</div>
                    <div className="text-sm text-gray-400">{doc.tier_name} (±{Math.round(doc.bonus_pct * 100)}%)</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Skill Selection */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Boosts */}
            <div>
              <h4 className="text-sm font-semibold text-green-500 mb-3">
                BOOST (+{Math.round(bonusPct * 100)}%)
              </h4>
              <div className="space-y-2">
                {skills.map((skill) => (
                  <button
                    key={`boost-${skill}`}
                    onClick={() => toggleBoost(skill)}
                    disabled={penalties.includes(skill) || (boosts.length >= 3 && !boosts.includes(skill))}
                    className={`w-full p-3 border rounded-lg text-left transition-colors capitalize disabled:opacity-50 disabled:cursor-not-allowed ${
                      boosts.includes(skill)
                        ? 'bg-green-900/20 border-green-900/50'
                        : 'bg-stone-900/50 border-stone-800 hover:border-green-900/30'
                    }`}
                  >
                    {skill}
                  </button>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                {boosts.length}/3 selected
              </div>
            </div>

            {/* Penalties */}
            <div>
              <h4 className="text-sm font-semibold text-red-500 mb-3">
                PENALIZE (-{Math.round(bonusPct * 100)}%)
              </h4>
              <div className="space-y-2">
                {skills.map((skill) => (
                  <button
                    key={`penalty-${skill}`}
                    onClick={() => togglePenalty(skill)}
                    disabled={boosts.includes(skill) || (penalties.length >= 3 && !penalties.includes(skill))}
                    className={`w-full p-3 border rounded-lg text-left transition-colors capitalize disabled:opacity-50 disabled:cursor-not-allowed ${
                      penalties.includes(skill)
                        ? 'bg-red-900/20 border-red-900/50'
                        : 'bg-stone-900/50 border-stone-800 hover:border-red-900/30'
                    }`}
                  >
                    {skill}
                  </button>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                {penalties.length}/3 required (must equal boosts)
              </div>
            </div>
          </div>

          {/* Validation Messages */}
          {boosts.length !== penalties.length && (boosts.length > 0 || penalties.length > 0) && (
            <div className="p-3 bg-amber-900/10 border border-amber-900/30 rounded-lg text-sm text-amber-400">
              ⚠️ Must select equal number of boosts and penalties
            </div>
          )}

          {/* Apply Button */}
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!canApply}
            className="w-full px-6 py-3 bg-amber-900/20 hover:bg-amber-900/30 border border-amber-900/50 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply Specialization
          </button>
        </div>
      </div>
    </div>
  );
};


// Assign Mentor Modal
export const AssignMentorModal = ({ gladiator, doctores, onClose, onSuccess }) => {
  const [selectedDoctore, setSelectedDoctore] = useState(doctores[0]?.glad_id || null);
  const [selectedSkill, setSelectedSkill] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Class-specific mentor skill options
  const CLASS_MENTOR_SKILLS = {
    "Dimachaerus": ["strength", "speed"],
    "Provocator": ["defense", "stamina"],
    "Retiarius": ["technique", "precision"],
    "Murmillo": ["defense", "stamina"],
  };

  // Try multiple possible class field names (cls is the correct one)
  const gladClass = gladiator.cls || gladiator.class || gladiator.class_info?.title || '';
  const availableSkills = CLASS_MENTOR_SKILLS[gladClass] || [];
  const doctore = doctores.find(d => d.glad_id === selectedDoctore);
  const mentorPct = doctore?.mentor_pct || 0;

  const assignMentor = async () => {
    if (!selectedDoctore || !selectedSkill) return;

    try {
      setLoading(true);
      setError('');

      await api.post(`/sanctum/doctores/${selectedDoctore}/assign`, {
        glad_id: gladiator.id,
        skill: selectedSkill
      });

      await onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign mentor');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 overflow-y-auto">
      <div className="bg-gradient-to-b from-stone-900 to-stone-950 border border-amber-900/30 rounded-lg w-full max-w-lg my-4">
        {/* Header */}
        <div className="bg-stone-900 border-b border-amber-900/30 p-3 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-amber-500">Assign Mentor to {gladiator.name}</h3>
            <p className="text-xs text-gray-400">
              {gladClass} {gladiator.class_info?.title && `(${gladiator.class_info.title})`}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-800 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[50vh] overflow-y-auto">
          {/* Info */}
          <div className="p-4 bg-blue-900/10 border border-blue-900/30 rounded-lg flex items-start gap-3">
            <UserCog className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-200">
              <p className="font-semibold mb-1">One-on-One Mentoring</p>
              <p className="text-blue-200/80">Each doctore can mentor ONE gladiator at a time, providing ongoing training bonus to a specific skill.</p>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-900/20 border border-red-900/50 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-red-400 text-sm">{error}</div>
            </div>
          )}

          {/* Select Doctore */}
          <div>
            <label className="block text-sm font-semibold text-gray-400 mb-3">Select Doctore:</label>
            <div className="space-y-2">
              {doctores.map((doc) => (
                <label
                  key={doc.glad_id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedDoctore === doc.glad_id
                      ? 'bg-amber-900/20 border-amber-900/50'
                      : 'bg-stone-900/50 border-stone-800 hover:border-amber-900/30'
                  } ${doc.assigned_to ? 'opacity-60' : ''}`}
                >
                  <input
                    type="radio"
                    name="doctore"
                    value={doc.glad_id}
                    checked={selectedDoctore === doc.glad_id}
                    onChange={() => setSelectedDoctore(doc.glad_id)}
                    className="text-amber-500"
                  />
                  <img 
                    src={CLASS_IMAGE[doc.class] || CLASS_IMAGE.Provocator} 
                    alt={doc.class}
                    className="w-10 h-10 rounded-full border-2 border-amber-900/50 object-cover"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-amber-100">{doc.name}</div>
                    <div className="text-sm text-gray-400">
                      {doc.tier_name} (+{Math.round(doc.mentor_pct * 100)}% per skill)
                    </div>
                    {doc.assigned_to && (
                      <div className="text-xs text-blue-400 mt-1">
                        Currently mentoring {doc.assigned_to.glad_name} ({doc.assigned_to.skill})
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Select Skill */}
          <div>
            <label className="block text-sm font-semibold text-gray-400 mb-3">
              Choose Skill to Mentor (+{Math.round(mentorPct * 100)}%):
            </label>
            <div className="grid grid-cols-2 gap-2">
              {availableSkills.map((skill) => (
                <button
                  key={skill}
                  onClick={() => setSelectedSkill(skill)}
                  className={`p-3 border rounded-lg text-left transition-colors capitalize ${
                    selectedSkill === skill
                      ? 'bg-blue-900/20 border-blue-900/50'
                      : 'bg-stone-900/50 border-stone-800 hover:border-blue-900/30'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
            {availableSkills.length === 0 && (
              <div className="p-3 bg-stone-900/30 border border-stone-800 rounded-lg text-sm text-gray-500">
                <p className="text-center">No mentoring skills available for this class</p>
                <p className="text-xs text-gray-600 text-center mt-2">
                  Debug: Class detected as &quot;{gladClass}&quot;
                </p>
              </div>
            )}
          </div>

          {/* Assign Button */}
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={assignMentor}
              disabled={!selectedDoctore || !selectedSkill || loading}
              className="flex-1 px-4 py-2 bg-amber-900/20 hover:bg-amber-900/30 border border-amber-900/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Assigning...' : 'Assign Mentor'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Upgrade Sanctum Modal
export const UpgradeSanctumModal = ({ currentSlots, cost, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUpgrade = async () => {
    try {
      setLoading(true);
      setError('');
      
      await api.post('/sanctum/upgrade');
      await onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upgrade sanctum');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const nextSlots = currentSlots + 1;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 overflow-y-auto">
      <div className="bg-gradient-to-b from-stone-900 to-stone-950 border border-amber-900/30 rounded-lg w-full max-w-sm my-4">
        {/* Header with Image */}
        <div className="relative w-full h-20 overflow-hidden rounded-t-lg">
          <img 
            src="/doctore-bg.png" 
            alt="Upgrade Sanctum"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-stone-900"></div>
          
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <h3 className="text-base font-semibold text-amber-500">Upgrade Sanctum</h3>
            </div>
          </div>
          
          <button 
            onClick={onClose} 
            className="absolute top-1 right-1 p-1.5 bg-black/50 hover:bg-black/70 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-900/50 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-red-400 text-sm">{error}</div>
            </div>
          )}

          {/* Upgrade Info */}
          <div className="text-center space-y-4">
            <div className="p-4 bg-stone-900/50 border border-amber-900/20 rounded-lg">
              <div className="text-gray-400 text-sm mb-2">Current Capacity</div>
              <div className="text-3xl font-bold text-amber-500">{currentSlots} {currentSlots === 1 ? 'Slot' : 'Slots'}</div>
            </div>

            <div className="flex items-center justify-center gap-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-amber-900/50"></div>
              <div className="text-amber-500 text-2xl">→</div>
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-amber-900/50"></div>
            </div>

            <div className="p-4 bg-emerald-900/20 border border-emerald-900/50 rounded-lg">
              <div className="text-gray-400 text-sm mb-2">After Upgrade</div>
              <div className="text-3xl font-bold text-emerald-500">{nextSlots} Slots</div>
            </div>

            <div className="p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
              <div className="text-gray-400 text-sm mb-2">Cost</div>
              <div className="text-2xl font-bold text-amber-400">{cost} Gold</div>
            </div>
          </div>

          {/* Benefits */}
          <div className="p-4 bg-stone-900/30 border border-stone-800 rounded-lg">
            <div className="text-sm text-gray-400 space-y-2">
              <p className="font-semibold text-amber-500 mb-2">Benefits:</p>
              <p>• Train {nextSlots} gladiators simultaneously with different specializations</p>
              <p>• Mentor {nextSlots} gladiators at once for continuous skill bonuses</p>
              <p>• Maximize your ludus training efficiency</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-3 bg-stone-800 hover:bg-stone-700 rounded-lg transition-colors disabled:opacity-50 font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="flex-1 px-4 py-3 bg-emerald-900/20 hover:bg-emerald-900/30 border border-emerald-900/50 rounded-lg transition-colors disabled:opacity-50 font-semibold disabled:cursor-not-allowed"
            >
              {loading ? 'Upgrading...' : `Upgrade for ${cost}g`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
